"use client";

import {
  ArrowRight,
  BarChart3,
  Calendar,
  Cloud,
  CreditCard,
  Database,
  FileText,
  Github,
  Globe,
  HardDrive,
  Heart,
  Leaf,
  Mail,
  MessageSquare,
  Phone,
  Search,
  ShoppingCart,
  Table2,
  Users,
  Webhook,
  Zap,
} from "lucide-react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from "@/components/ui/motion";

const categories = [
  { id: "all", label: "All" },
  { id: "communication", label: "Communication" },
  { id: "crm", label: "CRM & Sales" },
  { id: "developer", label: "Developer" },
  { id: "database", label: "Database" },
  { id: "marketing", label: "Marketing" },
  { id: "payments", label: "Payments" },
];

const integrations = [
  {
    name: "Slack",
    category: "communication",
    icon: MessageSquare,
    description: "Send messages and alerts",
  },
  {
    name: "Stripe",
    category: "payments",
    icon: CreditCard,
    description: "Payment processing",
  },
  {
    name: "GitHub",
    category: "developer",
    icon: Github,
    description: "Repo events & actions",
  },
  {
    name: "Salesforce",
    category: "crm",
    icon: Cloud,
    description: "CRM automation",
  },
  {
    name: "HubSpot",
    category: "crm",
    icon: Heart,
    description: "Marketing & sales hub",
  },
  {
    name: "Notion",
    category: "developer",
    icon: FileText,
    description: "Docs & databases",
  },
  {
    name: "Airtable",
    category: "database",
    icon: Table2,
    description: "Flexible databases",
  },
  {
    name: "Twilio",
    category: "communication",
    icon: Phone,
    description: "SMS & voice",
  },
  {
    name: "SendGrid",
    category: "communication",
    icon: Mail,
    description: "Email delivery",
  },
  {
    name: "AWS S3",
    category: "developer",
    icon: HardDrive,
    description: "Cloud storage",
  },
  {
    name: "PostgreSQL",
    category: "database",
    icon: Database,
    description: "SQL database",
  },
  {
    name: "MongoDB",
    category: "database",
    icon: Leaf,
    description: "NoSQL database",
  },
  {
    name: "Zapier",
    category: "developer",
    icon: Zap,
    description: "Connect 5000+ apps",
  },
  {
    name: "Google Calendar",
    category: "communication",
    icon: Calendar,
    description: "Schedule events",
  },
  {
    name: "Shopify",
    category: "payments",
    icon: ShoppingCart,
    description: "E-commerce platform",
  },
  {
    name: "Google Analytics",
    category: "marketing",
    icon: BarChart3,
    description: "Web analytics",
  },
  {
    name: "Intercom",
    category: "crm",
    icon: Users,
    description: "Customer messaging",
  },
  {
    name: "Webflow",
    category: "marketing",
    icon: Globe,
    description: "Website builder",
  },
];

export default function IntegrationsPage() {
  const navigate = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredIntegrations = integrations.filter((integration) => {
    const matchesSearch = integration.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory =
      activeCategory === "all" || integration.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-20 pointer-events-none" />
        <div className="absolute top-20 right-1/4 w-[500px] h-[500px] bg-accent/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <FadeInUp className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1.5 mb-4 text-xs font-semibold tracking-wider uppercase bg-primary/10 text-primary rounded-full border border-primary/20">
              Integrations
            </span>
            <h1 className="text-foreground mb-6">
              Connect to
              <br />
              <span className="gradient-text">everything</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              200+ pre-built integrations plus webhooks and HTTP for anything
              custom. If it has an API, you can connect it.
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 border-b border-border sticky top-16 bg-background/95 backdrop-blur-md z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search integrations..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={
                    activeCategory === category.id ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Grid */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredIntegrations.map((integration) => (
              <StaggerItem key={integration.name}>
                <motion.div
                  whileHover={{ y: -4, scale: 1.02 }}
                  className="group flex flex-col items-center p-6 rounded-xl card-interactive cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <integration.icon className="w-6 h-6 text-primary" />
                  </div>
                  <span className="text-base font-medium text-foreground text-center mb-1">
                    {integration.name}
                  </span>
                  <span className="text-xs text-muted-foreground text-center">
                    {integration.description}
                  </span>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {filteredIntegrations.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                No integrations found matching your search.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setActiveCategory("all");
                }}
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Custom Integration */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <FadeInUp className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Don't see what you need?
              </h2>
              <p className="text-lg text-muted-foreground">
                Use our HTTP node or webhooks to connect to any API in minutes.
              </p>
            </FadeInUp>

            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                whileHover={{ y: -4 }}
                className="p-8 rounded-2xl card-interactive"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Webhook className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Webhooks
                </h3>
                <p className="text-muted-foreground mb-4">
                  Receive events from any service that supports webhooks.
                  Trigger workflows instantly.
                </p>
                <Button variant="outline" size="sm">
                  Learn more <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ y: -4 }}
                className="p-8 rounded-2xl card-interactive"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  HTTP Requests
                </h3>
                <p className="text-muted-foreground mb-4">
                  Make custom HTTP requests to any API. Full control over
                  headers, body, and authentication.
                </p>
                <Button variant="outline" size="sm">
                  Learn more <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Ready to connect your stack?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start building integrations in minutes. Free tier included.
            </p>
            <Button
              variant="gradient"
              size="lg"
              onClick={() => navigate.push("/register")}
            >
              Start building free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
}
